import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import moment from "moment";
import styles from "./styles.module.scss";
import { getAutoCashoutPaymentCalls } from "../../../../setup/api/services/autoCashout";
import {
  EAutoCashoutOutcome,
  IAutoCashoutPaymentCall,
} from "../../../../setup/api/services/autoCashout/models";
import Pagination from "../../../../components/pagination";

const PAGE_SIZE = 10;

const OUTCOME_STYLE: Record<EAutoCashoutOutcome, string> = {
  [EAutoCashoutOutcome.CASHED_OUT_REQUESTED]: "success",
  [EAutoCashoutOutcome.SKIPPED]: "not_sent",
  [EAutoCashoutOutcome.FAILED]: "failed",
};

const OUTCOME_LABEL: Record<EAutoCashoutOutcome, string> = {
  [EAutoCashoutOutcome.CASHED_OUT_REQUESTED]:
    "lbl_auto_cashout_outcome_cashed_out_requested",
  [EAutoCashoutOutcome.SKIPPED]: "lbl_auto_cashout_outcome_skipped",
  [EAutoCashoutOutcome.FAILED]: "lbl_auto_cashout_outcome_failed",
};

interface IAutoCashoutAttempts {
  domainId: string;
}

const AutoCashoutAttempts = ({ domainId }: IAutoCashoutAttempts) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<IAutoCashoutPaymentCall[]>([]);
  // 1-based to match the shared Pagination component; the API is 0-based.
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [domainId]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    getAutoCashoutPaymentCalls(domainId, page - 1, PAGE_SIZE)
      .then((res) => {
        if (cancelled) return;
        setItems(res?.data?.content || []);
        setTotalPages(res?.data?.totalPages || 0);
        setTotalElements(res?.data?.totalElements || 0);
      })
      .catch((error) => {
        // Hidden on failure: roles without auto-cashout access get a 403.
        console.error("Error fetching auto-cashout payment calls:", error);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [domainId, page]);

  if (!items.length) return null;

  return (
    <div className={styles.card}>
      <h3 className={styles.title}>{t("lbl_auto_cashout_attempts")}</h3>
      <div
        className={classNames(
          styles.table_container,
          isLoading && styles.loading
        )}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t("lbl_auto_cashout_col_date")}</th>
              <th>{t("lbl_auto_cashout_col_amount")}</th>
              <th>{t("lbl_auto_cashout_col_beneficiary")}</th>
              <th>{t("lbl_auto_cashout_col_outcome")}</th>
              <th>{t("lbl_auto_cashout_col_reason")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td className={styles.date}>
                  {moment(item.createdAt)
                    .locale("en")
                    .format("YYYY-MM-DD HH:mm")}
                </td>
                <td className={styles.amount}>
                  {(item.amount ?? 0).toFixed(2) +
                    " " +
                    t("lbl_saudi_currency")}
                </td>
                <td>{item.beneficiaryName || item.beneficiaryId || "-"}</td>
                <td>
                  <span
                    className={classNames(
                      styles.pill,
                      styles[OUTCOME_STYLE[item.outcome] || "not_sent"]
                    )}>
                    {OUTCOME_LABEL[item.outcome]
                      ? t(OUTCOME_LABEL[item.outcome])
                      : item.outcome}
                  </span>
                </td>
                <td>{item.reason || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        countName={t("lbl_auto_cashout_attempt")}
        pageSize={PAGE_SIZE}
        pagesCount={totalPages}
        page={page}
        length={totalElements}
        handlePaginationChange={setPage}
      />
    </div>
  );
};

export default AutoCashoutAttempts;
